import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ManageEvents = () => {
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    number: '',
    date: '',
    pointOfContact: '',
    address: '',
    image: ''
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/events`);
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title,
      number: event.number,
      date: event.date,
      pointOfContact: event.pointOfContact,
      address: event.address,
      image: event.image,
    });
    setEditId(event._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_REQUESTING_API}/api/events/update/${editId}`,
        formData
      );
      alert('Event Updated Successfully!');
      setFormData({
        title: '',
        number: '',
        date: '',
        pointOfContact: '',
        address: '',
        image: ''
      });
      setEditId(null);
      fetchEvents();
    } catch (err) {
      console.error(err);
      alert('Error updating event');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_REQUESTING_API}/api/events/delete/${id}`
        );
        alert('Event Deleted Successfully!');
        fetchEvents();
      } catch (err) {
        console.error(err);
        alert('Error deleting event');
      }
    }
  };

  const filteredEvents = searchTerm
    ? events.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
        Manage Events
      </h2>

      {/* Search */}
      <div className="mb-8">
        <input
          type="text"
          placeholder="Search events by title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 rounded-xl
                     border border-gray-300
                     focus:outline-none focus:ring-2 focus:ring-gray-400
                     shadow-sm"
        />
      </div>

      {/* Edit Form */}
      {editId && (
        <form
          onSubmit={handleUpdate}
          className="bg-white rounded-2xl shadow-lg p-6 mb-12 space-y-4 border"
        >
          <h3 className="text-xl font-semibold text-gray-700 mb-4">
            Update Event
          </h3>

          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Event Title"
            required
            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
          />

          <input
            type="text"
            name="number"
            value={formData.number}
            onChange={handleChange}
            placeholder="Contact Number"
            required
            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
          />

          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
          />

          <input
            type="text"
            name="pointOfContact"
            value={formData.pointOfContact}
            onChange={handleChange}
            placeholder="Point of Contact"
            required
            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
          />

          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Address"
            required
            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
          />

          <input
            type="file"
            onChange={handleFileChange}
            accept="image/*"
            className="w-full text-sm"
          />

          <button
            type="submit"
            className="w-full py-3 rounded-xl
                       bg-green-50 text-green-700 font-semibold
                       border border-green-200
                       hover:bg-green-100 transition-colors"
          >
            Update Event
          </button>
        </form>
      )}

      {searchTerm && filteredEvents.length === 0 && (
        <p className="text-center text-gray-500">No events found.</p>
      )}

      {/* Events Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents.map(event => (
          <div
            key={event._id}
            className="bg-white rounded-2xl shadow-md
                       hover:shadow-xl transition-shadow
                       border overflow-hidden"
          >
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-48 object-cover"
            />

            <div className="p-5 space-y-2">
              <h3 className="text-xl font-bold text-gray-800">
                {event.title}
              </h3>

              <p className="text-sm text-gray-600">
                <span className="font-semibold">Date:</span>{' '}
                {new Date(event.date).toLocaleDateString()}
              </p>

              <p className="text-sm text-gray-600">
                <span className="font-semibold">Contact:</span>{' '}
                {event.pointOfContact} ({event.number})
              </p>

              <p className="text-sm text-gray-600">
                <span className="font-semibold">Address:</span>{' '}
                {event.address}
              </p>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleEdit(event)}
                  className="flex-1 py-2 rounded-lg
                             bg-yellow-50 text-yellow-900
                             border border-yellow-600
                             hover:bg-yellow-100 transition-colors"
                >
                  Edit
                </button>

                <button
                  onClick={() => handleDelete(event._id)}
                  className="flex-1 py-2 rounded-lg
                             bg-red-50 text-red-600
                             border border-red-200
                             hover:bg-red-100 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManageEvents;






// working 16/12/25

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';

// const ManageEvents = () => {
//   const [events, setEvents] = useState([]);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [formData, setFormData] = useState({
//     title: '',
//     number: '',
//     date: '',
//     pointOfContact: '',
//     address: '',
//     image: ''
//   });
//   const [editId, setEditId] = useState(null);

//   useEffect(() => {
//     fetchEvents();
//   }, []);

//   const fetchEvents = async () => {
//     try {
//       const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/events`);
//       setEvents(res.data);
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const handleEdit = (event) => {
//     setFormData({
//       title: event.title,
//       number: event.number,
//       date: event.date,
//       pointOfContact: event.pointOfContact,
//       address: event.address,
//       image: event.image,
//     });
//     setEditId(event._id);
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setFormData({...formData, image: reader.result});
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({...formData, [name]: value});
//   };

//   const handleUpdate = async (e) => {
//     e.preventDefault();
//     try {
//       await axios.put(`${process.env.REACT_APP_REQUESTING_API}/api/events/update/${editId}`, formData);
//       alert('Event Updated Successfully!');
//       setFormData({
//         title: '',
//         number: '',
//         date: '',
//         pointOfContact: '',
//         address: '',
//         image: ''
//       });
//       setEditId(null);
//       fetchEvents();
//     } catch (err) {
//       console.error(err);
//       alert('Error updating event');
//     }
//   };

//   const handleDelete = async (id) => {
//     if (window.confirm('Are you sure you want to delete this event?')) {
//       try {
//         await axios.delete(`${process.env.REACT_APP_REQUESTING_API}/api/events/delete/${id}`);
//         alert('Event Deleted Successfully!');
//         fetchEvents();
//       } catch (err) {
//         console.error(err);
//         alert('Error deleting event');
//       }
//     }
//   };

//   const filteredEvents = searchTerm
//     ? events.filter(event =>
//         event.title.toLowerCase().includes(searchTerm.toLowerCase())
//       )
//     : [];

//   return (
//     <div className="max-w-6xl mx-auto p-6">
//       <h2 className="text-3xl font-bold text-center mb-8">Update & Delete Events</h2>

//       {/* Search Field */}
//       <div className="mb-6">
//         <input
//           type="text"
//           placeholder="Search events by title..."
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           className="w-full p-3 border-2 border-gray-300  rounded-2xl"
//         />
//       </div>

//       {/* Only show form if editing */}
//       {editId && (
//         <form onSubmit={handleUpdate} className="space-y-4 bg-white p-6 rounded shadow-md mb-10">
//           <input
//             type="text"
//             name="title"
//             value={formData.title}
//             onChange={handleChange}
//             placeholder="Event Title"
//             required
//             className="w-full p-2 border rounded"
//           />
//           <input
//             type="text"
//             name="number"
//             value={formData.number}
//             onChange={handleChange}
//             placeholder="Contact Number"
//             required
//             className="w-full p-2 border rounded"
//           />
//           <input
//             type="date"
//             name="date"
//             value={formData.date}
//             onChange={handleChange}
//             required
//             className="w-full p-2 border rounded"
//           />
//           <input
//             type="text"
//             name="pointOfContact"
//             value={formData.pointOfContact}
//             onChange={handleChange}
//             placeholder="Point of Contact"
//             required
//             className="w-full p-2 border rounded"
//           />
//           <input
//             type="text"
//             name="address"
//             value={formData.address}
//             onChange={handleChange}
//             placeholder="Address"
//             required
//             className="w-full p-2 border rounded"
//           />
//           <input
//             type="file"
//             onChange={handleFileChange}
//             accept="image/*"
//             className="w-full"
//           />
//           <button type="submit" className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700">
//             Update Event
//           </button>
//         </form>
//       )}

//       {/* Events List (only shown if searchTerm is used) */}
//       {searchTerm && filteredEvents.length === 0 && (
//         <p className="text-center text-gray-500">No events found.</p>
//       )}

//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//         {filteredEvents.map(event => (
//           <div key={event._id} className="bg-white shadow-md rounded overflow-hidden relative">
//             <img src={event.image} alt={event.title} className="w-full h-48 object-cover" />
//             <div className="p-4">
//               <h3 className="text-xl font-bold mb-2">{event.title}</h3>
//               <p><strong>Date:</strong> {new Date(event.date).toLocaleDateString()}</p>
//               <p><strong>Contact:</strong> {event.pointOfContact} ({event.number})</p>
//               <p><strong>Address:</strong> {event.address}</p>
//               <div className="flex gap-2 mt-4">
//                 <button
//                   onClick={() => handleEdit(event)}
//                   className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-white py-1 rounded"
//                 >
//                   Edit
//                 </button>
//                 <button
//                   onClick={() => handleDelete(event._id)}
//                   className="flex-1 bg-red-600 hover:bg-red-700 text-white py-1 rounded"
//                 >
//                   Delete
//                 </button>
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default ManageEvents;


